import React, { useEffect } from "react";
import { Outlet, useParams, Navigate, useLocation } from "react-router-dom";
import { isValidLang } from "@/i18n/translations";
import { LangProvider, useLang } from "@/contexts/LangContext";
import { useAuth } from "@/contexts/AuthContext";
import PasswordGate from "@/components/PasswordGate";
import Header from "./Header";
import Footer from "./Footer";
import FilmGrain from "@/components/FilmGrain";
import CustomCursor from "@/components/CustomCursor";
import AmbientAudio from "@/components/AmbientAudio";
import LiveDayRibbon from "@/components/LiveDayRibbon";
import AndreasChat from "@/components/AndreasChat";
import PushOptInModal from "@/components/PushOptInModal";
import InstallPromptModal from "@/components/InstallPromptModal";
import LocationPromptModal from "@/components/LocationPromptModal";
import LocationPinger from "@/components/LocationPinger";
import PwaManifest from "@/components/PwaManifest";
import PwaWelcomeModal from "@/components/PwaWelcomeModal";
import PushReconciliationBanner from "@/components/PushReconciliationBanner";
import VersionRefreshToast from "@/components/VersionRefreshToast";

const Inner = () => {
  const { isAuthed, authReady } = useAuth();
  const { lang } = useLang();
  const location = useLocation();

  useEffect(() => {
    document.documentElement.lang = lang;
    localStorage.setItem("em_lang", lang);
  }, [lang]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [location.pathname]);

  if (!authReady) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="editorial-kicker animate-pulse" data-testid="page-loading">· · ·</div>
      </div>
    );
  }
  if (!isAuthed) return <PasswordGate />;

  return (
    <div className="min-h-screen flex flex-col">
      <PwaManifest scope="guest" />
      <FilmGrain />
      <CustomCursor />
      <AmbientAudio />
      <LiveDayRibbon />
      <Header />
      <main className="flex-1" data-testid="site-main">
        <Outlet />
      </main>
      <Footer />
      <AndreasChat />
      <InstallPromptModal />
      <PwaWelcomeModal />
      <PushOptInModal />
      <LocationPromptModal />
      <LocationPinger />
      <PushReconciliationBanner />
      <VersionRefreshToast />
    </div>
  );
};

export const Layout = () => {
  const { lang } = useParams();
  if (!isValidLang(lang)) {
    const stored = localStorage.getItem("em_lang");
    return <Navigate to={`/${stored && isValidLang(stored) ? stored : "en"}`} replace />;
  }
  return (
    <LangProvider>
      <Inner />
    </LangProvider>
  );
};

export default Layout;
